import { type RadioChangeEvent, type StepProps, notification, Radio } from 'antd5';
import { type Dispatch, type SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { useServerDataTableContext } from 'components/DataTable/ServerDataTable';
import { useUploadContext } from 'components/FileUploader';
import type { UploadFile } from 'components/FileUploader';
import { Steps as ProgressSteps, useMessage } from 'components/ui';
import type {
  AtlasGqlAsset,
  AtlasGqlAssetType,
  AtlasGqlInspectionTask,
  AtlasGqlInspectionType,
  AtlasGqlInternalBladeInspectionTask,
  AtlasGqlValidateImageMetadataFileInput,
} from 'types/atlas-graphql';
import {
  GetInspectionDraftImagesDocument,
  useAddInspectionDraftImageMutation,
  useBulkDeleteInspectionsMutation,
  useDeleteTasksMutation,
  useGetAssetForInspectionLazyQuery,
  useProcessImageMetadataFileMutation,
} from 'types/atlas-graphql';
import { INSPECTIONS_UPLOAD_CREATE } from 'utils/access-control/rules';
import { useAccountContext } from 'utils/account/AccountContext';
import { ASSET_TYPE_BLADE, ASSET_TYPE_TURBINE } from 'utils/constants';
import { useFeatures } from 'utils/features';
import { useAlertOnTryLeave, useForceLinksNewTab } from 'utils/navigation';
import { ENHANCED_INSPECTION_STATUSES } from 'utils/release-toggles';
import { usePermissions } from 'utils/usePermissions';

import {
  type BladeExternalAssetDescriptors,
  type BladeInternalAssetDescriptors,
  type TowerExternalAssetDescriptors,
  type TransitionPieceAssetDescriptors,
  parseInspectionUploadPath,
} from '../pathParsers';

import { CancelUploadModal } from './CancelUploadModal';
import { ConfirmUploadModal } from './ConfirmUploadModal';
import { useUploadInspectionDataModalContext } from './Context/UploadInspectionDataModalContext';
import { UploadInspectionDataModalContextProvider } from './Context/UploadInspectionDataModalContextProvider';
import { ImageDetails } from './ImageDetails';
import { ImageUpload } from './ImageUpload';
import {
  type MultipleInspectionValidationProps,
  MultipleInspections,
  MultipleInspectionValidation,
} from './MultipleInspections';
import {
  type SingleInspectionValidationProps,
  SingleInspection,
  SingleInspectionValidation,
} from './SingleInspection';
import { hasSkydioImportAccess } from './skydioAccess';
import { UploadAlert } from './UploadAlert';
import {
  InspectionTypeSelector,
  ModalBody,
  StyledModal,
  TitleContainer,
} from './UploadInspectionDataModal.style';

export type CloseOnSuccessProps = {
  uploadedImages?: UploadFile[];
};

interface UploadInspectionDataModalProps {
  open: boolean;
  onClose: () => void;
  onSingleInspectionCreate?: () => void;
  onRollback?: () => void;
  onComplete?: () => void;
  initialStep?: InitialStep;
  initialInspectionType?: AtlasGqlInspectionType;
  lockedUploadType?: UploadTypes;
  existingInspectionIds?: string[];
  inspectionTasks?: {
    taskId: string;
    inspectionId?: string;
    inspectionType?: AtlasGqlInspectionType;
    assetType?: AtlasGqlAssetType;
  }[];
  requireTaskId?: boolean;
  task?: (AtlasGqlInspectionTask | AtlasGqlInternalBladeInspectionTask) | null;
  asset?: AtlasGqlAsset | null;
}

export enum UploadTypes {
  Single,
  Multiple,
}

export enum Steps {
  SingleUpload,
  SingleUploadValidation,
  SummaryUpload,
  SummaryMapping,
  SummaryPreview,
  SummaryValidation,
  DetailsUpload,
  DetailsMapping,
  DetailsPreview,
  DetailsValidation,
  ImageUpload,
  ImageUploadValidation,
}
export type InitialStep = Steps.SingleUpload | Steps.SummaryUpload | Steps.DetailsUpload;

export type InspectionUploadWorkflowComponentProps = {
  uploadId: string;
  requireTaskId?: boolean;
  step: Steps;
  setStep: Dispatch<SetStateAction<Steps>>;
  inspectionTasks?: UploadInspectionDataModalProps['inspectionTasks'];
  onComplete?: () => void;
};

export const UploadInspectionDataModalInner = ({
  open,
  onClose,
  onSingleInspectionCreate,
  onRollback,
  onComplete,
  initialStep,
  initialInspectionType,
  lockedUploadType,
  existingInspectionIds,
  inspectionTasks,
  requireTaskId,
  task,
  asset,
}: UploadInspectionDataModalProps) => {
  useAlertOnTryLeave(open);
  useForceLinksNewTab(open);

  const { t } = useTranslation();
  const message = useMessage();
  const { features } = useFeatures();
  const { canAccess } = usePermissions();
  const canUseSkydioImport = hasSkydioImportAccess({
    canCreateInspectionUpload: canAccess([INSPECTIONS_UPLOAD_CREATE]),
    inspectionUploadEnabled: Boolean(features.INSPECTION_UPLOAD),
    skydioImportEnabled: Boolean(features.SKYDIO_IMPORT),
  });

  const { hasReleaseToggle } = useAccountContext();
  const hasEnhancedStatuses = hasReleaseToggle(ENHANCED_INSPECTION_STATUSES);

  // uploadId for entire process created up top; should be stable for inspection and image details files
  const uploadId = useRef<string>(
    lockedUploadType === UploadTypes.Single &&
      existingInspectionIds &&
      existingInspectionIds.length === 1
      ? `${existingInspectionIds[0]}_${uuid()}`
      : uuid()
  );

  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
  const [step, setStep] = useState<Steps>(
    initialStep ??
      (lockedUploadType === undefined
        ? Steps.SummaryUpload
        : lockedUploadType === UploadTypes.Multiple
          ? Steps.SummaryUpload
          : Steps.SingleUpload)
  );

  const [uploadType, setUploadType] = useState<UploadTypes>(
    lockedUploadType ?? UploadTypes.Multiple
  );
  const [singleValidationStepProps, setSingleValidationStepProps] =
    useState<Omit<SingleInspectionValidationProps, 'setStep' | 'rollback'>>();
  const [multipleValidationStepProps, setMultipleValidationStepProps] =
    useState<
      Omit<MultipleInspectionValidationProps, 'setStep' | 'setErroredUploadSteps' | 'rollback'>
    >();
  const [rollbackLoading, setRollbackLoading] = useState<boolean>(false);

  const validateImageMetadataInputRef = useRef<
    AtlasGqlValidateImageMetadataFileInput | undefined
  >();

  const {
    resetState,
    useFolderMethod,
    pendingStep,
    setPendingStep,
    erroredUploadSteps,
    setErroredUploadSteps,
    inspectionType,
    setInspectionType,
    createdInspectionIds,
    setCreatedInspectionIds,
    createdTaskIds,
    setCreatedTaskIds,
    shouldAutoRelease,
    autoReleaseStatus,
  } = useUploadInspectionDataModalContext();

  const targetInspectionIds = useMemo(
    () => [...createdInspectionIds, ...(existingInspectionIds ?? [])],
    [createdInspectionIds, existingInspectionIds]
  );

  const singleInspectionId = useMemo(() => {
    const allInspectionIds = [
      ...(existingInspectionIds ? existingInspectionIds : []),
      ...createdInspectionIds,
    ];
    if (allInspectionIds.length === 1) {
      return allInspectionIds[0];
    }
    return undefined;
  }, [existingInspectionIds, createdInspectionIds]);

  const { refetch } = useServerDataTableContext().context;
  const { setSelectedFiles, uploading, setUploading } = useUploadContext();

  const [deleteInspections] = useBulkDeleteInspectionsMutation();
  const [deleteTasks] = useDeleteTasksMutation();
  const [processImageMetadataFile] = useProcessImageMetadataFileMutation();
  const [addDraftImage] = useAddInspectionDraftImageMutation({
    refetchQueries: [
      {
        query: GetInspectionDraftImagesDocument,
        variables: { id: singleInspectionId },
      },
    ],
    awaitRefetchQueries: true,
  });
  const [assetForInspectionQuery] = useGetAssetForInspectionLazyQuery();

  const { title, currentStep, stepItems } = useMemo(() => {
    const createInspectionTitle = t('inspection.createInspection', 'Create Inspection');
    const uploadSummaryFileTitle = t(
      'inspection.uploadInspectionSummaryFile',
      'Upload Inspection Summary File'
    );
    const uploadImageDetailsFileTitle = t(
      'inspection.uploadImageDetailsFile',
      'Upload Image Details File'
    );
    const title = {
      [Steps.SingleUpload]: createInspectionTitle,
      [Steps.SingleUploadValidation]: createInspectionTitle,
      [Steps.SummaryUpload]: uploadSummaryFileTitle,
      [Steps.SummaryMapping]: uploadSummaryFileTitle,
      [Steps.SummaryPreview]: uploadSummaryFileTitle,
      [Steps.SummaryValidation]: uploadSummaryFileTitle,
      [Steps.DetailsUpload]: uploadImageDetailsFileTitle,
      [Steps.DetailsMapping]: uploadImageDetailsFileTitle,
      [Steps.DetailsPreview]: uploadImageDetailsFileTitle,
      [Steps.DetailsValidation]: uploadImageDetailsFileTitle,
      [Steps.ImageUpload]: t('inspection.uploadInspectionImages', 'Upload Inspection Images'),
      [Steps.ImageUploadValidation]: t('inspection.uploadImages', 'Upload Images'),
    }[step];

    /**
     * Rendered steps are conditional based on the initial step. Currently, only
     * SingleUpload, SummaryUpload, and DetailsUpload are supported as initial steps.
     * If other entry points are allowed, this will need to be updated.
     */
    const currentStep = (
      initialStep === Steps.DetailsUpload
        ? {
            [Steps.DetailsUpload]: 0,
            [Steps.DetailsMapping]: 0,
            [Steps.DetailsPreview]: 0,
            [Steps.DetailsValidation]: 1,
            [Steps.ImageUpload]: 2,
            [Steps.ImageUploadValidation]: 3,
          }
        : {
            [Steps.SingleUpload]: 0,
            [Steps.SingleUploadValidation]: 1,
            [Steps.SummaryUpload]: 0,
            [Steps.SummaryMapping]: 0,
            [Steps.SummaryPreview]: 0,
            [Steps.SummaryValidation]: 1,
            [Steps.DetailsUpload]: 2,
            [Steps.DetailsMapping]: 2,
            [Steps.DetailsPreview]: 2,
            [Steps.DetailsValidation]: 3,
            [Steps.ImageUpload]: 4,
            [Steps.ImageUploadValidation]: 5,
          }
    )[step];

    const uploadImageDetailsLabel = t('inspection.uploadImageDetails', 'Upload Image Details');
    const validateImageDetailsLabel = t(
      'inspection.validateImageDetails',
      'Validate Image Details'
    );
    const uploadImagesLabel = t('inspection.uploadImages', 'Upload Images');
    const reviewImagesLabel = t('inspection.reviewImages', 'Review Images');
    const validateSummaryLabel = t('inspection.validateSummary', 'Validate Summary');

    const stepLabels =
      initialStep === Steps.DetailsUpload
        ? [uploadImageDetailsLabel, validateImageDetailsLabel, uploadImagesLabel, reviewImagesLabel]
        : uploadType === UploadTypes.Single
          ? [
              t('inspection.createInspection', 'Create Inspection'),
              t('inspection.validateCreation', 'Validate Creation'),
              uploadImageDetailsLabel,
              validateImageDetailsLabel,
              uploadImagesLabel,
              reviewImagesLabel,
            ]
          : [
              t('inspection.uploadSummary', 'Upload Summary'),
              validateSummaryLabel,
              uploadImageDetailsLabel,
              validateImageDetailsLabel,
              uploadImagesLabel,
              reviewImagesLabel,
            ];

    const stepItems: StepProps[] = stepLabels.map(label => ({
      title: label,
      status:
        (label === validateSummaryLabel && erroredUploadSteps.summary) ||
        (label === validateImageDetailsLabel && erroredUploadSteps.imageDetails)
          ? 'error'
          : undefined,
    }));

    return { title, currentStep, stepItems };
  }, [step, initialStep, uploadType, erroredUploadSteps, t]);

  // set initial step  on init
  useEffect(() => {
    if (initialStep) {
      setStep(initialStep);
    }
  }, [initialStep]);

  // set inspection type on init
  useEffect(() => {
    if (initialInspectionType) {
      setInspectionType(initialInspectionType);
    }
  }, [initialInspectionType]);

  // reset upload errors on upload type change
  useEffect(() => {
    setErroredUploadSteps({ summary: false, imageDetails: false });
  }, [uploadType]);

  // If we create one inspection, set uploadId to include that inspection's uuid
  useEffect(() => {
    if (
      uploadType === UploadTypes.Single &&
      createdInspectionIds.length === 1 &&
      !uploadId.current.includes(createdInspectionIds[0])
    ) {
      uploadId.current = `${createdInspectionIds[0]}_${uuid()}`;
    }
  }, [createdInspectionIds, uploadType]);

  // handles deleting created inspections and tasks
  const handleRollback = async (_toastMessage?: string) => {
    if (createdInspectionIds.length > 0 || createdTaskIds.length > 0) {
      setRollbackLoading(true);

      // delete inspections
      if (createdInspectionIds.length > 0) {
        await deleteInspections({
          variables: { input: { ids: createdInspectionIds } },
        });
      }

      // delete tasks
      if (createdTaskIds.length > 0) {
        await deleteTasks({
          variables: { ids: createdTaskIds },
        });
      }

      setRollbackLoading(false);

      // conditionally refetch the parent list
      refetch && (await refetch());

      // conditionally perform additional logic on rollback
      onRollback && onRollback();
    }

    // conditionally set toast message
    const toastMessage = _toastMessage
      ? _toastMessage
      : createdInspectionIds.length > 0 && createdTaskIds.length > 0
        ? t(
            'inspection.inspectionTaskCreationRolledBack',
            'Inspection & Task creation successfully rolled back'
          )
        : createdInspectionIds.length > 0
          ? t(
              'inspection.inspectionCreationRolledBack',
              'Inspection creation successfully rolled back'
            )
          : undefined;
    toastMessage && message.success(toastMessage);

    // clear created ids
    setCreatedInspectionIds([]);
    setCreatedTaskIds([]);
    uploadId.current = uuid();
  };

  // handles the cancelling of the inspection modal
  const handleCancelModal = () => {
    if (createdInspectionIds.length > 0 || createdTaskIds.length > 0) {
      // opens cancellation modal if inspections/tasks created
      setCancelModalOpen(true);
    } else {
      // otherwises closes modal
      handleCloseModal();
    }
  };

  // handles the closing of the inspection modal
  const handleCloseModal = () => {
    resetState();
    setUploadType(lockedUploadType ?? UploadTypes.Multiple);
    setStep(initialStep ?? Steps.SummaryUpload);
    setCreatedInspectionIds([]);
    setCreatedTaskIds([]);
    // Set uploadId to a new value
    uploadId.current = uuid();

    // Clear out the selectedFiles and reset uploading state in the upload context
    setSelectedFiles([]);
    setUploading(false);
    onClose();
  };

  // handle processing image metadata file
  const handleProcessComplete = async ({ uploadedImages = [] }: CloseOnSuccessProps) => {
    const imageNames = uploadedImages.map(image => image.name);
    // If we have image metadata, call the processImageMetadaFile mutation, which will handle validating the metadata and creating draft images
    if (validateImageMetadataInputRef.current) {
      try {
        const { data } = await processImageMetadataFile({
          variables: {
            input: {
              ...validateImageMetadataInputRef.current,
              uploadedImages: imageNames,
              autoRelease: shouldAutoRelease,
              status: shouldAutoRelease && hasEnhancedStatuses ? autoReleaseStatus : undefined,
            },
          },
        });

        if ((data?.processImageMetadataFile?.failures ?? []).length === 0) {
          notification.open({
            message: t('inspection.uploadComplete', 'Inspection upload complete'),
            description: t(
              'inspection.uploadCompleteNotificationDescription',
              'You will receive a notification when each inspection has been processed.'
            ),
          });

          onComplete && onComplete();

          // close the modal;
          handleCloseModal();
        } else {
          // reset uploading so the button re-enables and the user can react to the error.
          setUploading(false);
          message.error(
            t(
              'inspection.errorProcessingImageDetails',
              'Error processing image details: one or more images failed to process'
            )
          );
        }
      } catch (error) {
        // reset uploading so the button re-enables and the user can react to the error.
        setUploading(false);
        message.error(
          t(
            'inspection.errorProcessingImageDetailsMessage',
            'Error processing image details: {{message}}',
            {
              message: error.message,
            }
          )
        );
      }
    } else if (singleInspectionId) {
      try {
        // If we don't have image details metadata, but we do have a single inspection id
        // We need to manually create the draft images for that inspection
        const uploadErrors = [];
        const { data } = await assetForInspectionQuery({
          variables: { inspectionId: singleInspectionId },
        });
        const inspectionAsset = data?.inspection?.asset;
        const isInspectedAssetBlade = data?.inspection?.asset?.assetType?.name === ASSET_TYPE_BLADE; // There has to be a const for this
        const bladesPositionMap: any = {};
        if (inspectionAsset?.assetType?.name === ASSET_TYPE_TURBINE) {
          inspectionAsset?.children?.forEach(child => {
            if (
              child?.assetType?.name === ASSET_TYPE_BLADE &&
              'position' in child &&
              child.position
            ) {
              bladesPositionMap[child.position] = child;
            }
          });
        }
        await Promise.all(
          uploadedImages.map(async imageInput => {
            // If we're using the folder method, calculate asset descriptors
            let assetDescriptors:
              | BladeExternalAssetDescriptors
              | BladeInternalAssetDescriptors
              | TowerExternalAssetDescriptors
              | TransitionPieceAssetDescriptors = {};

            if (useFolderMethod) {
              assetDescriptors = parseInspectionUploadPath(
                imageInput.sourceName,
                inspectionType?.name
              );
              // If we are targeting a blade-specific inspection upload
              // overwrite the blade position even if the folder structure provides a different position
              if (
                isInspectedAssetBlade &&
                inspectionAsset &&
                'position' in inspectionAsset &&
                inspectionAsset?.position
              ) {
                assetDescriptors.bladePosition = inspectionAsset.position;
              }

              // If bladePosition is part of the descriptors, extract the asset uuid for that position
              if (assetDescriptors?.bladePosition) {
                const bladeAsset = isInspectedAssetBlade
                  ? inspectionAsset
                  : bladesPositionMap[assetDescriptors.bladePosition];
                if (bladeAsset) {
                  assetDescriptors.bladeGlobalId = bladeAsset.id;
                }
              }
            }

            const { errors } = await addDraftImage({
              variables: {
                input: {
                  name: (imageInput.data?.name ?? imageInput.name).toLowerCase(),
                  path: imageInput.sourceName.toLowerCase(),
                  assetDescriptors,
                  inspectionId: singleInspectionId,
                },
              },
            });
            if (errors) {
              uploadErrors.push(errors);
            }
          })
        );

        // If there's an error, alert the user
        if (uploadErrors.length > 0) {
          message.error(
            t(
              'inspection.errorProcessingImageDetails',
              'Error processing image details: one or more images failed to process'
            )
          );
        } else {
          notification.open({
            message: t('inspection.uploadComplete', 'Inspection upload complete'),
            description: t(
              'inspection.draftImagesAdded',
              'Draft images have been added to the inspection.'
            ),
          });
        }
      } catch (error) {
        message.error(
          t(
            'inspection.errorProcessingImageDetailsMessage',
            'Error processing image details: {{message}}',
            {
              message: error.message,
            }
          )
        );
      }
      // close the modal
      handleCloseModal();
    }
  };

  return (
    <>
      <StyledModal
        data-testid="upload-inspection-data-modal"
        title={
          <TitleContainer>
            {title}
            <br />
            <ProgressSteps items={stepItems} current={currentStep} size="small" itemWidth="130px" />
          </TitleContainer>
        }
        open={open}
        onCancel={handleCancelModal}
        closable={!uploading}
        footer={null}
        width={842}
        maskClosable={false}
        destroyOnHidden
      >
        {[Steps.SingleUpload, Steps.SummaryUpload].includes(step) &&
          lockedUploadType === undefined && (
            <InspectionTypeSelector>
              <span>{t('inspection.numberOfInspections', 'Number of Inspections:')}</span>
              <Radio.Group
                buttonStyle="solid"
                size="small"
                options={[
                  { label: t('inspection.single', 'Single'), value: UploadTypes.Single },
                  { label: t('inspection.multiple', 'Multiple'), value: UploadTypes.Multiple },
                ]}
                defaultValue={uploadType}
                optionType="button"
                onChange={({ target: { value } }: RadioChangeEvent) => {
                  setUploadType(value);
                  const step =
                    value === UploadTypes.Multiple ? Steps.SummaryUpload : Steps.SingleUpload;
                  setStep(step);
                }}
              />
            </InspectionTypeSelector>
          )}
        <ModalBody>
          <UploadAlert />
          {step === Steps.SingleUpload && (
            <SingleInspection
              onClose={handleCancelModal}
              step={step}
              setStep={setStep}
              task={task}
              asset={asset}
              onSingleInspectionCreate={onSingleInspectionCreate}
              setValidationStepProps={setSingleValidationStepProps}
            />
          )}
          {[Steps.SummaryUpload, Steps.SummaryMapping, Steps.SummaryPreview].includes(step) && (
            <MultipleInspections
              uploadId={uploadId.current}
              requireTaskId={requireTaskId}
              step={step}
              setStep={setStep}
              setValidationStepProps={setMultipleValidationStepProps}
              inspectionTasks={inspectionTasks}
              inspectionType={initialInspectionType}
            />
          )}
          {step === Steps.SingleUploadValidation && singleValidationStepProps && (
            <SingleInspectionValidation
              {...singleValidationStepProps}
              setStep={setStep}
              rollback={{ update: handleRollback, loading: rollbackLoading }}
            />
          )}
          {step === Steps.SummaryValidation && multipleValidationStepProps && (
            <MultipleInspectionValidation
              {...multipleValidationStepProps}
              setStep={setStep}
              rollback={{ update: handleRollback, loading: rollbackLoading }}
              existingInspectionIds={existingInspectionIds}
            />
          )}
          {[
            Steps.DetailsUpload,
            Steps.DetailsMapping,
            Steps.DetailsPreview,
            Steps.DetailsValidation,
          ].includes(step) && (
            <ImageDetails
              uploadId={uploadId.current}
              uploadType={uploadType}
              inspectionIds={targetInspectionIds}
              inspectionTasks={inspectionTasks}
              step={step}
              setStep={setStep}
              initialStep={initialStep}
              onCloseModal={handleCloseModal}
              onProcessComplete={handleProcessComplete}
              onComplete={onComplete}
              validateImageMetadataInputRef={validateImageMetadataInputRef}
              canUseSkydioImport={canUseSkydioImport}
            />
          )}
          {[Steps.ImageUpload, Steps.ImageUploadValidation].includes(step) && (
            <ImageUpload
              uploadId={uploadId.current}
              step={step}
              setStep={setStep}
              onBack={() => {
                // If details were added, go back to preview; if not, go back to details upload step
                setStep(
                  validateImageMetadataInputRef.current ? Steps.DetailsPreview : Steps.DetailsUpload
                );
              }}
              onSuccess={handleProcessComplete}
              singleInspectionId={singleInspectionId}
              skippedDetails={!validateImageMetadataInputRef.current}
            />
          )}
        </ModalBody>
      </StyledModal>
      <CancelUploadModal
        open={cancelModalOpen}
        handleContinue={() => setCancelModalOpen(false)}
        handleCancel={() => {
          setCancelModalOpen(false);
          handleCloseModal();
        }}
        rollback={{ update: handleRollback, loading: rollbackLoading }}
      />
      <ConfirmUploadModal
        open={!!pendingStep}
        handleContinue={() => {
          pendingStep && setStep(pendingStep);
          setPendingStep(undefined);
        }}
        handleCancel={() => setPendingStep(undefined)}
      />
    </>
  );
};

export const UploadInspectionDataModal = (props: UploadInspectionDataModalProps) => (
  <UploadInspectionDataModalContextProvider>
    <UploadInspectionDataModalInner {...props} />
  </UploadInspectionDataModalContextProvider>
);
